import {
  importProvidersFrom,
  inject,
  isDevMode,
  LOCALE_ID,
  provideAppInitializer,
  provideZoneChangeDetection,
} from '@angular/core';
import { APP_CONFIG, AppConfig } from './environments/app-environment.models';
import { APP_LOCALE, AppLocale } from './shared-angular/models/app-locale.model';
import { environment } from './environments/environment';
import { bootstrapApplication, BrowserModule } from '@angular/platform-browser';
import { provideUIRouter } from '@uirouter/angular';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { FormsModule } from '@angular/forms';
import { NgbModule } from '@ng-bootstrap/ng-bootstrap';
import { ToastModule } from './shared-angular/modules/toast/toast.module';
import { AppStates } from './app/app-routes';
import { ClientLanguageService } from './shared-utilities/services/client-language.service';
import { UIRouterConfig } from './app/uiRouter.config';
import { DatePipe } from '@angular/common';
import { OptionsCache } from './app/utilities/optionsCache/optionsCache';
import { AuthClientConfig, authHttpInterceptorFn, provideAuth0 } from '@auth0/auth0-angular';
import { provideHttpClient, withInterceptors } from '@angular/common/http';
import { provideAnimations } from '@angular/platform-browser/animations';
import { provideNgxMask } from 'ngx-mask';
import { AppComponent } from './app/app.component';
import { TransitionService } from './app/transition.service';
import { maintenanceModeInterceptor } from './app/client/journey/shared/services/maintenance-mode-http-interceptor';

const applicationLocale = getLocale();

function getLocale(): AppLocale {
  const urlPathParts = window.location.pathname.split('/');
  if (urlPathParts.length > 1 && urlPathParts[1].toLowerCase() === AppLocale.fr.toLowerCase()) {
    return AppLocale.fr;
  }
  return AppLocale.en;
}

// Will resolve the config from environment.appConfig or will
// resolve the config by fetching /assets/config.json
const resolveAppConfig = async (): Promise<AppConfig> => {
  let appConfig = environment.appConfig;
  if (isDevMode() == false) {
    const configResponse = await fetch(`/${applicationLocale}/assets/config.json`);
    appConfig = await configResponse.json();
  }
  return appConfig;
};

const appInitializer =
  (auth0ClientConfig: AuthClientConfig, appConfig: AppConfig, transitionService: TransitionService) => () => {
    auth0ClientConfig.set(appConfig.auth0);
    transitionService.hookTransitionEvents();
  };

const bootstrapAngular = async () => {
  const appConfig = await resolveAppConfig();

  bootstrapApplication(AppComponent, {
    providers: [
      provideZoneChangeDetection(),
      { provide: APP_CONFIG, useValue: appConfig },
      { provide: APP_LOCALE, useValue: applicationLocale },
      {
        provide: LOCALE_ID,
        deps: [ClientLanguageService],
        useFactory: (clientLanguageService: ClientLanguageService) => clientLanguageService.localeId,
      },
      importProvidersFrom(BrowserModule, FontAwesomeModule, FormsModule, NgbModule, ToastModule),
      provideUIRouter({
        states: AppStates,
        useHash: false,
        otherwise: { state: 'client.journey.home' },
        initial: { state: 'client.journey.home' },
        config: UIRouterConfig.initialize,
      }),
      { provide: ClientLanguageService, useClass: ClientLanguageService },
      DatePipe,
      OptionsCache,
      provideAuth0(),
      provideAppInitializer(() => {
        const authClientConfig = inject(AuthClientConfig);
        const appConfig = inject(APP_CONFIG);
        const transitionService = inject(TransitionService);

        const initializerFn = appInitializer(authClientConfig, appConfig, transitionService);
        return initializerFn();
      }),
      provideHttpClient(withInterceptors([maintenanceModeInterceptor, authHttpInterceptorFn])),
      provideAnimations(),
      provideNgxMask({
        thousandSeparator: ',',
        decimalMarker: '.',
      }),
    ],
  }).catch(err => console.error(err));
};

// Top level await is not yet supported, so raising the lambda function works around this
bootstrapAngular();
